import { CATEGORY_TAG, createTagsForList, QUESTION_TAG, SUBMISSION_TAG } from './utils/tags'
import {
    normalizeFetchQuestionChangesHistoryResponse,
    normalizeFetchQuestionMetadataResponse,
    normalizeFetchQuestionsResponse,
} from './utils/normalizers/question.normalizers'
import {
    onFetchQuestionProgressesQueryStarted,
    onTrainQuestionQueryStarted,
    onUpdateQuestionQueryStarted,
} from './utils/response-handlers/question.api.response-handlers'
import baseApi from './base.api'
import { createSearchParams } from '../../utils/request.helper'
import { onUpdatePatternQueryStarted } from './utils/response-handlers/pattern.api.response-handlers'

export const questionApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        fetchQuestions: builder.query({
            query: ({ token, versionId }) => ({
                url: `/questions/${token}/detailed`,
                method: 'GET',
                params: { version_id: versionId },
            }),
            transformResponse: normalizeFetchQuestionsResponse,
            providesTags: result => [
                ...createTagsForList(result, QUESTION_TAG),
                ...(result || []).map(question => createTagsForList(question.categories, CATEGORY_TAG)).flat(),
            ],
        }),
        updateQuestion: builder.mutation({
            query: ({
                id,
                name,
                weight,
                content,
                gradingReferenceContent,
                sectionId,
                wordsLimit,
                type,
                dependsOnQuestionIds,
                isGlobal,
            }) => ({
                url: `/questions/${id}`,
                method: 'PATCH',
                body: {
                    name,
                    text: content,
                    section: sectionId,
                    word_limit: wordsLimit,
                    type,
                    depends_on: dependsOnQuestionIds,
                    grading_reference_content: gradingReferenceContent,
                    weight,
                    is_global: isGlobal,
                },
            }),
            onQueryStarted: onUpdateQuestionQueryStarted,
        }),
        createQuestion: builder.mutation({
            query: ({ token, question }) => ({
                url: `/questions/${token}`,
                method: 'POST',
                body: {
                    name: question.name,
                    text: question.content,
                    type: question.type,
                    weight: question.weight,
                    word_limit: question.wordsLimit,
                    depends_on: question.dependsOnQuestionIds,
                    grading_reference_content: question.gradingReferenceContent,
                },
            }),
            invalidatesTags: [QUESTION_TAG],
        }),
        createComplexQuestion: builder.mutation({
            query: ({ token, name, children }) => ({
                url: `/questions/${token}/create-component`,
                method: 'POST',
                body: {
                    name,
                    children,
                },
            }),
            invalidatesTags: [QUESTION_TAG],
        }),
        batchUpdateQuestions: builder.mutation({
            query: ({ token, questions }) => ({
                url: `/text-models/${token}/update-questions-recursive`,
                method: 'POST',
                body: questions?.map(
                    ({ id, name, content, gradingReferenceContent, weight, wordsLimit, categories }) => ({
                        id,
                        name,
                        text: content,
                        grading_reference_content: gradingReferenceContent,
                        weight,
                        word_limit: wordsLimit,
                        categories: categories?.map(({ id, name, feedback, grade }) => ({ id, name, feedback, grade })),
                    }),
                ),
            }),
            invalidatesTags: [QUESTION_TAG],
        }),
        fetchQuestionMetadata: builder.query({
            query: ({ id }) => ({
                url: `/questions/${id}/metadata`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchQuestionMetadataResponse,
        }),
        updateCategory: builder.mutation({
            query: ({ id, colorId, name, description, type, feedback, grade, attributes, conceptualLabels }) => ({
                url: `/ftm-patterns/${id}`,
                method: 'PUT',
                body: {
                    type,
                    name,
                    color_id: colorId,
                    description,
                    feedback,
                    grade,
                    attributes,
                    conceptual_labels: conceptualLabels,
                },
            }),
            onQueryStarted: onUpdatePatternQueryStarted,
            invalidatesTags: (result, error, { id }) => [{ type: CATEGORY_TAG, id }],
        }),
        trainQuestion: builder.mutation({
            query: ({ id }) => ({
                url: `/questions/${id}/train`,
                method: 'POST',
            }),
            onQueryStarted: onTrainQuestionQueryStarted,
        }),
        fetchQuestionProgresses: builder.query({
            query: ({ ids }) => ({
                url: `/questions/processing-progress`,
                method: 'GET',
                params: createSearchParams({ question_id: ids }),
            }),
            onQueryStarted: onFetchQuestionProgressesQueryStarted,
        }),
        createCategory: builder.mutation({
            query: ({ questionId, name, feedback, description, grade, colorId, conceptualLabels }) => ({
                url: '/ftm-patterns/',
                method: 'POST',
                body: {
                    question_id: questionId,
                    name,
                    feedback,
                    description,
                    grade,
                    color_id: colorId,
                    conceptual_labels: conceptualLabels,
                },
            }),
            invalidatesTags: [{ type: CATEGORY_TAG }],
        }),
        reorderCategories: builder.mutation({
            query: ({ questionId, idToOrderMap }) => ({
                url: `/ftm-patterns/${questionId}/reorder`,
                method: 'PATCH',
                body: idToOrderMap,
            }),
            invalidatesTags: [{ type: CATEGORY_TAG }],
        }),
        fetchQuestionChangesHistory: builder.query({
            query: ({ id, versionId }) => ({
                url: `/questions/${id}/changes-history`,
                method: 'GET',
                params: { versionId },
            }),
            transformResponse: normalizeFetchQuestionChangesHistoryResponse,
            providesTags: [{ type: CATEGORY_TAG }, { type: QUESTION_TAG, id: 'LIST' }], //todo add question
        }),
        deleteCategory: builder.mutation({
            query: ({ categoryId }) => ({
                url: `/ftm-patterns/${categoryId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { categoryId }) => [
                { type: CATEGORY_TAG, id: categoryId },
                { type: SUBMISSION_TAG },
                { type: QUESTION_TAG, id: 'LIST' },
            ],
        }),
    }),
})

export const {
    useFetchQuestionsQuery,
    useUpdateQuestionMutation,
    useFetchQuestionMetadataQuery,
    useUpdateCategoryMutation,
    useTrainQuestionMutation,
    useLazyFetchQuestionProgressesQuery,
    useCreateCategoryMutation,
    useReorderCategoriesMutation,
    useBatchUpdateQuestionsMutation,
    useFetchQuestionChangesHistoryQuery,
    useDeleteCategoryMutation,
    useCreateQuestionMutation,
    useCreateComplexQuestionMutation,
} = questionApi
